import { getAccessToken } from '@/lib/tokenStorage';

import { enhancedApi } from '../api/baseApi';

export interface AvailableSireDto {
  sireId: string;
  sireName: string;
  sireBreed?: string;
  sireBreedId?: string;
  semenType?: string;
  semenTypeCode?: number;
  collectionCode?: string;
  accountId?: string;
  accountName?: string;
  naab?: string;
  cane?: string;
  semenId?: string;
  separateFert?: boolean;
  backupSireId?: string;
  backupSireName?: string;
  backupSemenType?: number;
}

export interface AvailableSiresPagedResponse {
  items: AvailableSireDto[];
  totalCount: number;
}

interface CreateFertWorkSessionParams {
  aspirationDate: string;
  siteId: string;
  sessionType?: number;
}

export interface ExcludedDonor {
  donorName: string;
  breedName: string;
  reason: string;
}

interface FertWorkSession {
  id: string;
  sessionId: string;
  aspirationDate: string;
  siteId: string;
  siteName: string;
  status: number;
  createdById: string;
  createdByName: string;
  runCount: number;
  invalidRunCount: number;
  warningRunCount: number;
  labelCount: number;
  incubatorMapCount: number;
  createdAt: string;
  updatedAt: string;
  lastRunAt: string | null;
  sessionType: number;
  excludedDonors?: ExcludedDonor[];
}

interface FertWorkSessionPayload {
  code: number;
  success: boolean;
  message: string;
  session: FertWorkSession | null;
}

interface GetSessionsParams {
  aspirationDate?: string;
  siteId?: string;
  siteName?: string;
  status?: string;
  createdById?: string;
  createdByName?: string;
  sessionId?: string;
  startRow?: number;
  endRow?: number;
  orderBy?: string;
}

export interface FertWorkSessionStatus {
  sessionId: string;
  status: number;
  progressPercentage?: number;
  processedRecordCount?: number;
  totalRecordsToProcess?: number;
  issueCount?: number;
  splitCount?: number;
  revisionCount?: number;
  errorMessage?: string;
  jobId?: string;
  jobStartedAt?: string;
  jobCompletedAt?: string;
}

export interface FertWorkDetailRecord {
  id: string;
  opuSiteId?: string;
  opuSiteName?: string;
  matTime?: string;
  donorId?: string;
  donorName?: string;
  donorLocation?: string;
  donorBreedId?: string;
  donorBreed?: string;
  speciesSubfamily?: string;
  breedCode?: string;
  totalOocytes: number;
  splitOocytes?: number;
  sireId?: string;
  sireName?: string;
  sireNote?: string;
  // 35106: read-only Semen Pull sire note reference (separate from the editable sireNote).
  semenPullSireNote?: string;
  sireBreedId?: string;
  sireBreed?: string;
  semenComments?: string;
  semenOwnerName?: string;
  separateFert: boolean;
  semenType?: string;
  pelletNumber?: number;
  accountId?: string;
  accountName?: string;
  shipLabelId?: string;
  shipLabel?: string;
  exportCode?: string;
  isSplit: boolean;
  splitRatio?: string;
  hasRevisionAvailable: boolean;
  revision?: string;
  revisionComment?: string;
  hasValidationIssues: boolean;
  issueCount: number;
  status: string;
  priority?: number | null;
  semenUnits?: number | null;
  isMassProduction?: boolean;
  poolNumber?: number | null;
  strawCount?: number | null;
  pelletVolumeUl?: number | null;
  strawCountOverridden?: boolean;
  pelletVolumeOverridden?: boolean;
}

export interface FertWorkValidationIssue {
  id: string;
  sessionDetailId: string;
  issueType: string;
  issueSeverity: string;
  issueCategory: string;
  fieldName: string;
  issueDescription: string;
  expectedValue?: string;
  actualValue?: string;
  isResolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  isEscalated: boolean;
  escalatedAt?: string;
  escalatedBy?: string;
  escalationNote?: string;
}

export interface IssueByDonor {
  donorName: string;
  issueCount: number;
  issueTypes: string[];
}

export interface PossibleSplit {
  donorName: string;
  totalOocytes: number;
  splitRatio: string;
  detailId: string;
}

export interface RevisionAvailable {
  donorName: string;
  detailId: string;
  revisionComment?: string;
}

export interface FertWorkAnalytics {
  totalIssues: number;
  issuesBySeverity: Record<string, number>;
  issuesByDonor: IssueByDonor[];
  possibleSplits: PossibleSplit[];
  revisionsAvailable: RevisionAvailable[];
  blockingIssueCount: number;
  blockingIssueTypes: string[];
  canGenerateRuns: boolean;
  escalatedIssueCount: number;
  hasPendingEscalations: boolean;
}

export interface FertWorkSessionDetail {
  session: FertWorkSession;
  details: FertWorkDetailRecord[];
  issues: FertWorkValidationIssue[];
  analytics: FertWorkAnalytics;
}

export interface SireInfo {
  id: string;
  name: string;
  note?: string;
  breed: string;
  semenType?: string;
  collectionCode?: string;
  oocytes: number;
  donorCount: number;
  pelletNumber?: string;
  isSexedFrozen: boolean;
  minMatTime?: string;
  donors: DonorInfo[];
}

export interface DonorInfo {
  id: string;
  name: string;
  breed: string;
  sireId?: string;
  sireName?: string;
  oocytes: number;
  isSplit: boolean;
  splitRatio?: string;
  semenType?: string;
  matTime?: string;
}

export interface Run {
  id: string;
  runId: string;
  customName?: string;
  sessionId: string;
  runNumber: number;
  sires: SireInfo[];
  sireCount: number;
  totalOocytes: number;
  matTimeWindow: string;
  minMatTime?: string;
  maxMatTime?: string;
  matTimeWindowMinutes?: number;
  runStartTime?: string;
  runEndTime?: string;
  donorCount: number;
  semenType: string;
  semenTypes: string[];
  semenTypesDisplay: string;
  pelletNumber?: string;
  totalPelletNumber?: string;
  donors: DonorInfo[];
  isValid: boolean;
  hasTimeWarning: boolean;
  validationLevel: 'valid' | 'warning' | 'invalid';
  validationMessage?: string;
  suggestedAction?: string;
  warningMessage?: string;
  exceedsSireLimit: boolean;
  exceedsDonorThreshold: boolean;
  exceedsTimeWindow: boolean;
  speciesSubfamily?: string;
}

export interface LabelMatingInfo {
  detailId: string;
  donorName: string;
  donorBreed: string;
  speciesSubfamily?: string;
  breedCode?: string;
  sireName: string;
  sireBreed: string;
  semenType: string;
  shipLabel: string;
  oocytes: number;
  matTime?: string;
  opuSiteName?: string | null;
  priority?: number | null;
  poolNumber?: number | null;
}

export interface Label {
  id: string;
  sessionId: string;
  runId: string;
  runNumber: number;
  labelNumber: number;
  labelId: string;
  top?: LabelMatingInfo;
  bottom?: LabelMatingInfo;
  isHalfPlate: boolean;
  planCategory: string;
  aspirationType: string;
  aspirationDate?: string;
  displayOrder: number;
  isDay5: boolean;
  isMassProduction?: boolean;
}

export interface LabelReorderItem {
  labelId: string;
  newDisplayOrder: number;
}

export interface IncubatorMapSlot {
  id: string;
  sessionId: string;
  labelId?: string;
  incubatorType: string;
  incubatorName?: string;
  slotPosition: string;
  shelfNumber?: number;
  rowNumber?: number;
  columnNumber?: number;
  chamberNumber?: number;
  chamberSlot?: number;
  planCategory?: string;
  isDay5: boolean;
  isOccupied: boolean;
  isDisabled: boolean;
  donorName?: string;
  donorBreed?: string;
  speciesSubfamily?: string;
  sireName?: string;
  shipLabel?: string;
  displayOrder: number;
  isDuplicate?: boolean;
  isMassProduction?: boolean;
  label?: Label;
}

export interface IncubatorSummary {
  incubatorName: string;
  incubatorType: string;
  totalSlots: number;
  occupiedSlots: number;
  availableSlots: number;
}

export interface IncubatorMapResponse {
  sessionId: string;
  sessionType: number;
  mapDate?: string;
  incubatorType: string;
  incubatorName?: string;
  totalSlots: number;
  occupiedSlots: number;
  availableSlots: number;
  hasDay5Conflict: boolean;
  day5ConflictMessage?: string;
  incubatorCount: number;
  incubators: IncubatorSummary[];
  slots: IncubatorMapSlot[];
}

export interface SessionLockStatus {
  sessionId: string;
  isLocked: boolean;
  lockedAt?: string | null;
  lockedBy?: string | null;
  version: number;
  lastValidatedAt?: string | null;
}

export interface IncubatorMapReorderItem {
  slotId: string;
  newSlotPosition: string;
  targetIncubatorName?: string;
}

export interface IncubatorTypeInfo {
  name: string;
  capacity: number;
}

export interface GenerateIncubatorMapParams {
  sessionId: string;
  incubatorCounts: Record<string, number>;
  preferenceOrder: string[];
  retainExisting?: boolean;
}

export interface ValidateMapMoveParams {
  sessionId: string;
  slotId: string;
  targetSlotPosition: string;
  targetIncubatorName?: string;
}

export interface ValidateMapMoveResponse {
  isValid: boolean;
  errorMessage?: string;
}

export interface ValidateLabelMoveParams {
  sessionId: string;
  sourceDetailId: string;
  targetLabelId: string;
  targetPosition: string;
}

export interface ValidateLabelMoveResponse {
  isValid: boolean;
  errorMessage?: string;
  warningMessage?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const fertWorkApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    createFertWorkSession: build.mutation<
      FertWorkSession,
      CreateFertWorkSessionParams
    >({
      queryFn: async (params) => {
        try {
          const token = getAccessToken();
          const response = await fetch(`${API_BASE_URL}/api/fertwork/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
            },
            credentials: 'include',
            body: JSON.stringify(params),
          });

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result: FertWorkSessionPayload = await response.json();

          if (!result.success || !result.session) {
            return {
              error: {
                status: result.code,
                data: result.message || 'Failed to create session',
              },
            };
          }

          return { data: result.session };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessions'],
    }),

    getFertWorkSessions: build.query<
      FertWorkSession[],
      GetSessionsParams | void
    >({
      queryFn: async (params = {}) => {
        try {
          const token = getAccessToken();
          const queryParams = new URLSearchParams();

          if (params.aspirationDate)
            queryParams.append('aspirationDate', params.aspirationDate);
          if (params.siteId) queryParams.append('siteId', params.siteId);
          if (params.siteName) queryParams.append('siteName', params.siteName);
          if (params.status) queryParams.append('status', params.status);
          if (params.createdById)
            queryParams.append('createdById', params.createdById);
          if (params.createdByName)
            queryParams.append('createdByName', params.createdByName);
          if (params.sessionId)
            queryParams.append('sessionId', params.sessionId);
          if (params.startRow)
            queryParams.append('startRow', params.startRow.toString());
          if (params.endRow)
            queryParams.append('endRow', params.endRow.toString());
          if (params.orderBy) queryParams.append('orderBy', params.orderBy);

          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/sessions?${queryParams.toString()}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (response.status === 204) {
            return { data: [] };
          }

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const sessions: FertWorkSession[] = await response.json();
          return { data: sessions };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      providesTags: ['FertWorkSessions'],
    }),

    getSessionStatus: build.query<FertWorkSessionStatus, string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/status`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const status: FertWorkSessionStatus = await response.json();
          return { data: status };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      keepUnusedDataFor: 0,
      async onQueryStarted(sessionId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.status === 2) {
            dispatch(
              fertWorkApi.util.invalidateTags(['FertWorkSessionDetail']),
            );
          }
        } catch {}
      },
    }),

    getSessionDetail: build.query<FertWorkSessionDetail, string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/detail`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const detail: FertWorkSessionDetail = await response.json();
          return { data: detail };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      providesTags: ['FertWorkSessionDetail'],
    }),

    updateSessionDetail: build.mutation<
      FertWorkDetailRecord,
      {
        sessionId: string;
        detailId: string;
        data: Partial<FertWorkDetailRecord>;
      }
    >({
      queryFn: async ({ sessionId, detailId, data }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/detail/${detailId}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify(data),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const detail: FertWorkDetailRecord = await response.json();
          return { data: detail };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessionDetail'],
    }),

    deleteSessionDetail: build.mutation<
      void,
      { sessionId: string; detailId: string }
    >({
      queryFn: async ({ sessionId, detailId }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/detail/${detailId}`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessionDetail'],
    }),

    splitSessionDetail: build.mutation<
      FertWorkDetailRecord[],
      { sessionId: string; detailId: string }
    >({
      queryFn: async ({ sessionId, detailId }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/detail/${detailId}/split`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const records: FertWorkDetailRecord[] = await response.json();
          return { data: records };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessionDetail'],
    }),

    deleteSession: build.mutation<void, string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessions'],
    }),

    refreshSession: build.mutation<{ session: { sessionId: string } }, string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/refresh`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const data = await response.json();
          return { data };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: [
        'FertWorkSessions',
        'FertWorkSession',
        'FertWorkSessionDetail',
        'FertWorkRuns',
        'FertWorkUnassignedDonors',
        'FertWorkLabels',
        'FertWorkIncubatorMaps',
      ],
    }),

    syncSession: build.mutation<
      {
        success: boolean;
        message: string;
        newRecordCount: number;
        newRecordIds: string[];
        totalRecordCount: number;
      },
      string
    >({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/sync`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const data = await response.json();
          return { data };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: [
        'FertWorkSessionDetail',
        'FertWorkRuns',
        'FertWorkUnassignedDonors',
      ],
    }),

    fixIssues: build.mutation<
      { message: string; sessionId: string },
      { sessionId: string; issueIds: string[] }
    >({
      queryFn: async ({ sessionId, issueIds }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/fix-issues`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({ issueIds }),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessionDetail'],
    }),

    escalateIssues: build.mutation<
      { message: string; sessionId: string },
      { sessionId: string; issueIds: string[]; note?: string }
    >({
      queryFn: async ({ sessionId, issueIds, note }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/escalate-issues`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({ issueIds, note }),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessionDetail'],
    }),

    resolveEscalation: build.mutation<
      { message: string; sessionId: string },
      { sessionId: string; issueIds: string[]; resolution?: string }
    >({
      queryFn: async ({ sessionId, issueIds, resolution }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/resolve-escalation`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({ issueIds, resolution }),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkSessionDetail'],
    }),

    generateRuns: build.mutation<
      Run[],
      { sessionId: string; matTimeWindowHours?: number }
    >({
      queryFn: async ({ sessionId, matTimeWindowHours = 3 }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/generate-runs?matTimeWindowHours=${matTimeWindowHours}`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const runs: Run[] = await response.json();
          return { data: runs };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: [
        'FertWorkRuns',
        'FertWorkUnassignedDonors',
        'FertWorkSessionDetail',
      ],
    }),

    regenerateRuns: build.mutation<
      Run[],
      { sessionId: string; matTimeWindowHours?: number }
    >({
      queryFn: async ({ sessionId, matTimeWindowHours }) => {
        try {
          const token = getAccessToken();
          const params = matTimeWindowHours
            ? `?matTimeWindowHours=${matTimeWindowHours}`
            : '';
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/regenerate-runs${params}`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const runs: Run[] = await response.json();
          return { data: runs };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkRuns', 'FertWorkUnassignedDonors'],
    }),

    getRuns: build.query<Run[], string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/runs`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (response.status === 204) {
            return { data: [] };
          }

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const runs: Run[] = await response.json();
          return { data: runs };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      providesTags: ['FertWorkRuns'],
    }),

    moveDonorBetweenRuns: build.mutation<
      void,
      {
        sessionId: string;
        donorDetailId: string;
        sourceRunId: string;
        targetRunId: string;
      }
    >({
      queryFn: async ({
        sessionId,
        donorDetailId,
        sourceRunId,
        targetRunId,
      }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/runs/move-donor`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({ donorDetailId, sourceRunId, targetRunId }),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkRuns'],
    }),

    deleteRun: build.mutation<void, { sessionId: string; runId: string }>({
      queryFn: async ({ sessionId, runId }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/runs/${runId}`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkRuns'],
    }),

    setRunName: build.mutation<
      void,
      { sessionId: string; runId: string; customName: string | null }
    >({
      queryFn: async ({ sessionId, runId, customName }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/runs/${runId}/name`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              body: JSON.stringify({ customName }),
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkRuns'],
    }),

    createEmptyRun: build.mutation<
      Run,
      { sessionId: string; afterRunId?: string }
    >({
      queryFn: async ({ sessionId, afterRunId }) => {
        try {
          const token = getAccessToken();
          const params = afterRunId ? `?afterRunId=${afterRunId}` : '';
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/runs${params}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const run: Run = await response.json();
          return { data: run };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkRuns'],
    }),

    getUnassignedDonors: build.query<DonorInfo[], string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/unassigned-donors`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (response.status === 204) {
            return { data: [] };
          }

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const donors: DonorInfo[] = await response.json();
          return { data: donors };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      providesTags: ['FertWorkUnassignedDonors'],
    }),

    assignDonorToRun: build.mutation<
      void,
      { sessionId: string; runId: string; donorDetailId: string }
    >({
      queryFn: async ({ sessionId, runId, donorDetailId }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/runs/${runId}/assign-donor`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({ donorDetailId }),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkRuns', 'FertWorkUnassignedDonors'],
    }),

    generateLabels: build.mutation<
      Label[],
      { sessionId: string; retainExisting?: boolean }
    >({
      queryFn: async ({ sessionId, retainExisting }) => {
        try {
          const token = getAccessToken();
          const params = retainExisting ? '?retainExisting=true' : '';
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/generate-labels${params}`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const labels: Label[] = await response.json();
          return { data: labels };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkLabels', 'FertWorkSessionDetail'],
    }),

    getLabels: build.query<Label[], string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/labels`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (response.status === 204) {
            return { data: [] };
          }

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const labels: Label[] = await response.json();
          return { data: labels };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      providesTags: ['FertWorkLabels'],
    }),

    reorderLabels: build.mutation<
      { success: boolean },
      { sessionId: string; newOrder: LabelReorderItem[] }
    >({
      queryFn: async ({ sessionId, newOrder }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/labels/reorder`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify(newOrder),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkLabels'],
    }),

    moveDetailBetweenLabels: build.mutation<
      { success: boolean },
      {
        sessionId: string;
        sourceDetailId: string;
        targetLabelId: string;
        targetPosition: string;
      }
    >({
      queryFn: async ({
        sessionId,
        sourceDetailId,
        targetLabelId,
        targetPosition,
      }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/labels/move-detail`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({
                sourceDetailId,
                targetLabelId,
                targetPosition,
              }),
            },
          );

          if (!response.ok) {
            const error = await response
              .json()
              .catch(() => ({ message: 'Failed to move detail' }));
            return {
              error: {
                status: response.status,
                data: error.message || 'Failed to move detail',
              },
            };
          }

          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkLabels'],
    }),

    validateLabelMove: build.query<
      ValidateLabelMoveResponse,
      ValidateLabelMoveParams
    >({
      queryFn: async ({
        sessionId,
        sourceDetailId,
        targetLabelId,
        targetPosition,
      }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/labels/validate-move-detail`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({
                sourceDetailId,
                targetLabelId,
                targetPosition,
              }),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result: ValidateLabelMoveResponse = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
    }),

    createEmptyLabel: build.mutation<
      Label,
      { sessionId: string; afterLabelId?: string }
    >({
      queryFn: async ({ sessionId, afterLabelId }) => {
        try {
          const token = getAccessToken();
          const params = afterLabelId ? `?afterLabelId=${afterLabelId}` : '';
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/labels${params}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const label: Label = await response.json();
          return { data: label };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkLabels'],
    }),

    generateIncubatorMaps: build.mutation<
      IncubatorMapResponse,
      GenerateIncubatorMapParams
    >({
      queryFn: async ({
        sessionId,
        incubatorCounts,
        preferenceOrder,
        retainExisting,
      }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/generate-maps`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({
                incubatorCounts,
                preferenceOrder,
                retainExisting: !!retainExisting,
              }),
            },
          );

          if (!response.ok) {
            const error = await response.json();
            return {
              error: {
                status: response.status,
                data: error.message || 'Failed to generate maps',
              },
            };
          }

          const result: IncubatorMapResponse = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkIncubatorMaps', 'FertWorkSessionDetail'],
    }),

    getIncubatorMaps: build.query<IncubatorMapResponse | null, string>({
      queryFn: async (sessionId) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/maps`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (response.status === 204) {
            return { data: null };
          }

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result: IncubatorMapResponse = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      providesTags: ['FertWorkIncubatorMaps'],
    }),

    getIncubatorTypes: build.query<IncubatorTypeInfo[], void>({
      queryFn: async () => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/incubator-types`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result: IncubatorTypeInfo[] = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
    }),

    lockSession: build.mutation<SessionLockStatus, { sessionId: string }>({
      queryFn: async ({ sessionId }) => {
        try {
          const token = getAccessToken();
          const res = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/lock`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );
          if (!res.ok)
            return { error: { status: res.status, data: await res.text() } };
          return { data: await res.json() };
        } catch (e: any) {
          return { error: { status: 'FETCH_ERROR', data: e.message } };
        }
      },
      invalidatesTags: ['FertWorkSession', 'FertWorkSessionDetail'],
    }),

    unlockSession: build.mutation<SessionLockStatus, { sessionId: string }>({
      queryFn: async ({ sessionId }) => {
        try {
          const token = getAccessToken();
          const res = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/unlock`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );
          if (!res.ok)
            return { error: { status: res.status, data: await res.text() } };
          return { data: await res.json() };
        } catch (e: any) {
          return { error: { status: 'FETCH_ERROR', data: e.message } };
        }
      },
      invalidatesTags: ['FertWorkSession', 'FertWorkSessionDetail'],
    }),

    revalidateSession: build.mutation<SessionLockStatus, { sessionId: string }>(
      {
        queryFn: async ({ sessionId }) => {
          try {
            const token = getAccessToken();
            const res = await fetch(
              `${API_BASE_URL}/api/fertwork/session/${sessionId}/revalidate`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                  'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
                },
                credentials: 'include',
              },
            );
            if (!res.ok)
              return { error: { status: res.status, data: await res.text() } };
            return { data: await res.json() };
          } catch (e: any) {
            return { error: { status: 'FETCH_ERROR', data: e.message } };
          }
        },
        invalidatesTags: ['FertWorkSession', 'FertWorkSessionDetail'],
      },
    ),

    toggleSlotDisabled: build.mutation<
      IncubatorMapResponse,
      { sessionId: string; slotId: string }
    >({
      queryFn: async ({ sessionId, slotId }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/maps/slot/${slotId}/toggle-disabled`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );
          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }
          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkIncubatorMaps'],
    }),

    duplicateIvcSlot: build.mutation<
      IncubatorMapResponse,
      { sessionId: string; slotId: string }
    >({
      queryFn: async ({ sessionId, slotId }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/maps/slot/${slotId}/duplicate`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );
          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }
          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkIncubatorMaps'],
    }),

    reorderIncubatorMaps: build.mutation<
      { success: boolean },
      { sessionId: string; moves: IncubatorMapReorderItem[] }
    >({
      queryFn: async ({ sessionId, moves }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/maps/reorder`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify(moves),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      invalidatesTags: ['FertWorkIncubatorMaps'],
    }),

    validateMapMove: build.query<
      ValidateMapMoveResponse,
      ValidateMapMoveParams
    >({
      queryFn: async ({
        sessionId,
        slotId,
        targetSlotPosition,
        targetIncubatorName,
      }) => {
        try {
          const token = getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/fertwork/session/${sessionId}/maps/validate-move`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
              body: JSON.stringify({
                slotId,
                targetSlotPosition,
                targetIncubatorName,
              }),
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result: ValidateMapMoveResponse = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
    }),

    getAvailableSires: build.query<
      AvailableSiresPagedResponse,
      {
        date: string;
        fertilizationSiteId: string;
        skip?: number;
        take?: number;
        search?: string;
      }
    >({
      queryFn: async ({
        date,
        fertilizationSiteId,
        skip = 0,
        take = 50,
        search,
      }) => {
        try {
          const token = getAccessToken();
          const params = new URLSearchParams({
            date,
            fertilizationSiteId,
            skip: String(skip),
            take: String(take),
          });
          if (search) params.set('search', search);
          const response = await fetch(
            `${API_BASE_URL}/api/DonorSchedule/available-sires?${params.toString()}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
              },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const error = await response.text();
            return { error: { status: response.status, data: error } };
          }

          const result: AvailableSiresPagedResponse = await response.json();
          return { data: result };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: error.message } };
        }
      },
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
  }),
});

export const {
  useCreateFertWorkSessionMutation,
  useGetFertWorkSessionsQuery,
  useGetSessionStatusQuery,
  useGetSessionDetailQuery,
  useUpdateSessionDetailMutation,
  useDeleteSessionDetailMutation,
  useSplitSessionDetailMutation,
  useDeleteSessionMutation,
  useRefreshSessionMutation,
  useSyncSessionMutation,
  useFixIssuesMutation,
  useEscalateIssuesMutation,
  useResolveEscalationMutation,
  useGenerateRunsMutation,
  useRegenerateRunsMutation,
  useGetRunsQuery,
  useMoveDonorBetweenRunsMutation,
  useDeleteRunMutation,
  useSetRunNameMutation,
  useCreateEmptyRunMutation,
  useGetUnassignedDonorsQuery,
  useAssignDonorToRunMutation,
  useGenerateLabelsMutation,
  useGetLabelsQuery,
  useReorderLabelsMutation,
  useCreateEmptyLabelMutation,
  useMoveDetailBetweenLabelsMutation,
  useLazyValidateLabelMoveQuery,
  useGenerateIncubatorMapsMutation,
  useGetIncubatorMapsQuery,
  useGetIncubatorTypesQuery,
  useReorderIncubatorMapsMutation,
  useToggleSlotDisabledMutation,
  useDuplicateIvcSlotMutation,
  useLockSessionMutation,
  useUnlockSessionMutation,
  useRevalidateSessionMutation,
  useLazyValidateMapMoveQuery,
  useGetAvailableSiresQuery,
} = fertWorkApi;
